import React, { useState } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import {
  Box,
  Drawer,
  Toolbar,
  List,
  Divider,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  useTheme,
  Skeleton,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Person as PersonIcon,
  MedicalServices as MedicalServicesIcon,
  EventNote as EventNoteIcon,
  FolderShared as FolderSharedIcon,
  Assessment as AssessmentIcon,
  Settings as SettingsIcon,
  Security as SecurityIcon,
  ExpandLess,
  ExpandMore,
  SupervisedUserCircle as SupervisedUserCircleIcon,
  School as SchoolIcon,
  Message as MessageIcon,
  CalendarMonth as CalendarMonthIcon,
  AddCircleOutline as AddCircleOutlineIcon,
  List as ListIcon,
  Category as CategoryIcon,
  SettingsApplications,
  Description,
  FolderSpecial as RecursosIcon
} from '@mui/icons-material';
import { useAuth } from '../../hooks/useAuth';

const Sidebar = ({ open, mobileOpen, drawerWidth, handleDrawerToggle, handleMobileDrawerToggle }) => {
  const theme = useTheme();
  const location = useLocation();
  const { user, loading } = useAuth();
  
  // Estado para los submenús desplegables
  const [openMenus, setOpenMenus] = useState({
    pacientes: false,
    especialistas: false,
    citas: false,
    historias: false,
    reportes: false,
    configuracion: false,
  });

  const handleMenuToggle = (menu) => {
    setOpenMenus({
      ...openMenus,
      [menu]: !openMenus[menu],
    });
  };

  // Verificar si una ruta está activa
  const isActive = (path) => {
    if (path === '/dashboard') {
      return location.pathname === path;
    }
    return location.pathname.startsWith(path);
  };

  // 🔧 FUNCIÓN HELPER PARA OBTENER EL ROL CORRECTAMENTE
  const getUserRole = () => {
    if (!user) return null;
    
    // Si user.rol es un objeto, acceder a la propiedad nombre
    if (typeof user.rol === 'object' && user.rol !== null) {
      return user.rol.nombre || user.rol.name || user.rol.tipo || null;
    }
    
    // Si es string, devolverlo directamente
    if (typeof user.rol === 'string') {
      return user.rol;
    }
 
    
    return null;
  };

  // 👥 PERMISOS SEGÚN ROLES ESPECÍFICOS - CON MANEJO CORRECTO DE OBJETO ROL
  const hasPermission = (section) => {
    // Durante la carga, mostrar elementos básicos para evitar flash
    if (loading || !user) {
      return ['dashboard', 'pacientes', 'citas'].includes(section);
    }
    
    const userRole = getUserRole();
    
    if (!userRole) {
      console.warn('⚠️ No se pudo determinar el rol del usuario:', {
        user,
        userRol: user.rol
      });
      return false;
    }
    
    
    
    switch (userRole) {
      case 'Administrador':
        // 🔑 ADMINISTRADOR: Acceso total a todo
        return true;
        
      case 'Especialista':
        // 👨‍⚕️ ESPECIALISTA: Dashboard básico, citas asignadas, HCE de sus pacientes
        return [
          'dashboard',    // Dashboard personalizado
          'pacientes',    // Solo sus pacientes asignados
          'citas',        // Sus citas asignadas
          'historias',    // HCE completo de sus pacientes
          'evaluaciones'  // Evaluaciones de sus pacientes
        ].includes(section);
        
      case 'Usuario Interno':
      case 'Interno':
        // 👨‍🎓 USUARIO INTERNO: Limitado a pacientes del supervisor
        return [
          'dashboard',    // Dashboard con tareas asignadas
          'pacientes',    // Solo pacientes del supervisor
          'citas',        // Solo citas del supervisor
          'historias'     // Solo HCE del supervisor
        ].includes(section);
        
      case 'Admisión':
        // 📝 ADMISIÓN: CRUD pacientes y citas, SIN acceso clínico
        return [
          'dashboard',    // Dashboard básico con estadísticas
          'pacientes',    // CRUD completo de pacientes
          'citas'         // CRUD completo de citas
          // ❌ SIN acceso a historias clínicas
        ].includes(section);
        
      case 'Recepcionista':
        // 🏥 RECEPCIONISTA: Gestión administrativa
        return [
          'dashboard',
          'pacientes',
          'citas',
          'pagos'
        ].includes(section);
        
      case 'Asistente':
        // 👩‍💼 ASISTENTE: Apoyo básico
        return [
          'dashboard',
          'pacientes',
          'citas'
        ].includes(section);
        
      default:
        console.warn(`⚠️ Rol no reconocido: "${userRole}"`);
        // Retornar permisos básicos para roles no reconocidos
        return ['dashboard'].includes(section);
    }
  };

  // Verificar permisos para crear/editar
  const canCreate = () => {
    if (loading || !user) return false;
    
    const userRole = getUserRole();
    return [
      'Administrador',
      'Admisión'  // Admisión puede crear pacientes y citas
    ].includes(userRole);
  };

  // Verificar acceso a configuración (solo administradores)
  const canAccessConfiguration = () => {
    if (loading || !user) return false;
    const userRole = getUserRole();
    return userRole === 'Administrador';
  };

  // Verificar acceso a especialistas (solo administradores)
  const canAccessSpecialists = () => {
    if (loading || !user) return false;
    const userRole = getUserRole();
    return userRole === 'Administrador';
  };

  // Verificar acceso a reportes
  const canAccessReports = () => {
    if (loading || !user) return false;
    const userRole = getUserRole();
    return ['Administrador'].includes(userRole);
  };

  const LoadingSkeleton = () => (
    <List component="nav">
      {[1, 2, 3, 4].map((item) => (
        <ListItem key={item} disablePadding>
          <ListItemButton>
            <ListItemIcon>
              <Skeleton variant="circular" width={24} height={24} />
            </ListItemIcon>
            <ListItemText 
              primary={<Skeleton variant="text" width="60%" />} 
            />
          </ListItemButton>
        </ListItem>
      ))}
    </List>
  );

 
  const drawerContent = (
    <>
      <Toolbar
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: [1],
        }}
      >
        <Box
          component="img"
          src={`${process.env.PUBLIC_URL}/assets/logo.png`}
          alt="NeuroPsico Cusco"
          sx={{ height: 50, my: 2 }}
        />
      </Toolbar>
      <Divider />
      
      {/* Mostrar skeleton mientras carga, o contenido normal */}
      {loading && !user ? (
        <LoadingSkeleton />
      ) : (
        <List component="nav">
          {/* 📊 DASHBOARD - Todos los roles tienen dashboard */}
          {hasPermission('dashboard') && (
            <ListItem disablePadding>
              <ListItemButton component={RouterLink} to="/dashboard" selected={isActive('/dashboard')}>
                <ListItemIcon>
                  <DashboardIcon color={isActive('/dashboard') ? 'primary' : 'inherit'} />
                </ListItemIcon>
                <ListItemText primary="Dashboard" />
              </ListItemButton>
            </ListItem>
          )}

          {/* 👥 PACIENTES */}
          {hasPermission('pacientes') && (
            <>
              <ListItem disablePadding>
                <ListItemButton onClick={() => handleMenuToggle('pacientes')} selected={isActive('/pacientes')}>
                  <ListItemIcon><PersonIcon color={isActive('/pacientes') ? 'primary' : 'inherit'} /></ListItemIcon>
                  <ListItemText primary="Pacientes" />
                  {openMenus.pacientes ? <ExpandLess /> : <ExpandMore />}
                </ListItemButton>
              </ListItem>
              <Collapse in={openMenus.pacientes} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  <ListItemButton component={RouterLink} to="/pacientes" sx={{ pl: 4 }} selected={location.pathname === '/pacientes'}>
                    <ListItemIcon><ListIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Listado" />
                  </ListItemButton>
                  {canCreate() && (
                    <ListItemButton component={RouterLink} to="/pacientes/nuevo" sx={{ pl: 4 }} selected={location.pathname === '/pacientes/nuevo'}>
                      <ListItemIcon><AddCircleOutlineIcon fontSize="small" /></ListItemIcon>
                      <ListItemText primary="Nuevo Paciente" />
                    </ListItemButton>
                  )}
                </List>
              </Collapse>
            </>
          )}
        
          {/* 📅 CITAS */}
          {hasPermission('citas') && (
            <>
              <ListItem disablePadding>
                <ListItemButton onClick={() => handleMenuToggle('citas')} selected={isActive('/citas')}>
                  <ListItemIcon><EventNoteIcon color={isActive('/citas') ? 'primary' : 'inherit'} /></ListItemIcon>
                  <ListItemText primary="Citas" />
                  {openMenus.citas ? <ExpandLess /> : <ExpandMore />}
                </ListItemButton>
              </ListItem>
              <Collapse in={openMenus.citas} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  <ListItemButton component={RouterLink} to="/citas" sx={{ pl: 4 }} selected={location.pathname === '/citas'}>
                    <ListItemIcon><ListIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Listado" />
                  </ListItemButton>
                  <ListItemButton component={RouterLink} to="/citas/calendario" sx={{ pl: 4 }} selected={location.pathname === '/citas/calendario'}>
                    <ListItemIcon><CalendarMonthIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Calendario" />
                  </ListItemButton>
                  {canCreate() && (
                    <ListItemButton component={RouterLink} to="/citas/nueva" sx={{ pl: 4 }} selected={location.pathname === '/citas/nueva'}>
                      <ListItemIcon><AddCircleOutlineIcon fontSize="small" /></ListItemIcon>
                      <ListItemText primary="Nueva Cita" />
                    </ListItemButton>
                  )}
                </List>
              </Collapse>
            </>
          )}

          {/* 📋 HISTORIAS CLÍNICAS - Solo especialistas e internos */}
          {hasPermission('historias') && (
            <>
              <ListItem disablePadding>
                <ListItemButton
                  onClick={() => handleMenuToggle('historias')}
                  selected={isActive('/historias')}
                >
                  <ListItemIcon>
                    <FolderSharedIcon color={isActive('/historias') ? 'primary' : 'inherit'} />
                  </ListItemIcon>
                  <ListItemText primary="Historias Clínicas" />
                  {openMenus.historias ? <ExpandLess /> : <ExpandMore />}
                </ListItemButton>
              </ListItem>
              <Collapse in={openMenus.historias} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  <ListItemButton
                    component={RouterLink}
                    to="/historias"
                    sx={{ pl: 4 }}
                    selected={location.pathname === '/historias'}
                  >
                    <ListItemIcon>
                      <ListIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary="Listado" />
                  </ListItemButton>
                </List>
              </Collapse>
            </>
          )}

          {/* 👨‍⚕️ ESPECIALISTAS - Solo administradores */}
          {canAccessSpecialists() && (
            <>
              <ListItem disablePadding>
                <ListItemButton
                  onClick={() => handleMenuToggle('especialistas')}
                  selected={isActive('/especialistas')}
                >
                  <ListItemIcon>
                    <MedicalServicesIcon color={isActive('/especialistas') ? 'primary' : 'inherit'} />
                  </ListItemIcon>
                  <ListItemText primary="Especialistas" />
                  {openMenus.especialistas ? <ExpandLess /> : <ExpandMore />}
                </ListItemButton>
              </ListItem>
              <Collapse in={openMenus.especialistas} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  <ListItemButton
                    component={RouterLink}
                    to="/especialistas"
                    sx={{ pl: 4 }}
                    selected={location.pathname === '/especialistas'}
                  >
                    <ListItemIcon>
                      <ListIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary="Listado" />
                  </ListItemButton>
                  <ListItemButton
                    component={RouterLink}
                    to="/especialistas/nuevo"
                    sx={{ pl: 4 }}
                    selected={location.pathname === '/especialistas/nuevo'}
                  >
                    <ListItemIcon>
                      <AddCircleOutlineIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary="Nuevo Especialista" />
                  </ListItemButton>
                </List>
              </Collapse>
            </>
          )}

          {/* 📊 REPORTES - Solo administradores */}
          {canAccessReports() && (
            <>
              <ListItem disablePadding>
                <ListItemButton
                  onClick={() => handleMenuToggle('reportes')}
                  selected={isActive('/reportes')}
                >
                  <ListItemIcon>
                    <AssessmentIcon color={isActive('/reportes') ? 'primary' : 'inherit'} />
                  </ListItemIcon>
                  <ListItemText primary="Reportes" />
                  {openMenus.reportes ? <ExpandLess /> : <ExpandMore />}
                </ListItemButton>
              </ListItem>
              <Collapse in={openMenus.reportes} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  <ListItemButton
                    component={RouterLink}
                    to="/reportes"
                    sx={{ pl: 4 }}
                    selected={location.pathname === '/reportes'}
                  >
                    <ListItemIcon>
                      <AssessmentIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary="Estadísticas" />
                  </ListItemButton>
                </List>
              </Collapse>
            </>
          )}

          {/* 🔒 AUDITORÍA - Solo administradores */}
          {canAccessConfiguration() && (
            <ListItem disablePadding>
              <ListItemButton
                component={RouterLink}
                to="/auditoria"
                selected={isActive('/auditoria')}
              >
                <ListItemIcon>
                  <SecurityIcon color={isActive('/auditoria') ? 'primary' : 'inherit'} />
                </ListItemIcon>
                <ListItemText primary="Auditoría" />
              </ListItemButton>
            </ListItem>
          )}

          <Divider sx={{ my: 1 }} />

          <ListItemButton component={RouterLink} to="/recursos-materiales">
            <ListItemIcon>
              <RecursosIcon />
            </ListItemIcon>
            <ListItemText primary="Recursos y Materiales" />
          </ListItemButton>

          {/* ⚙️ CONFIGURACIÓN - Solo administradores */}
          {canAccessConfiguration() && (
            <>
              <ListItem disablePadding>
                <ListItemButton
                  onClick={() => handleMenuToggle('configuracion')}
                  selected={isActive('/configuracion')}
                >
                  <ListItemIcon>
                    <SettingsIcon color={isActive('/configuracion') ? 'primary' : 'inherit'} />
                  </ListItemIcon>
                  <ListItemText primary="Configuración" />
                  {openMenus.configuracion ? <ExpandLess /> : <ExpandMore />}
                </ListItemButton>
              </ListItem>
              <Collapse in={openMenus.configuracion} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  <ListItemButton
                    component={RouterLink}
                    to="/configuracion"
                    sx={{ pl: 4 }}
                    selected={location.pathname === '/configuracion'}
                  >
                    <ListItemIcon>
                      <SettingsIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary="General" />
                  </ListItemButton>
                  <ListItemButton
                    component={RouterLink}
                    to="/configuracion/usuarios"
                    sx={{ pl: 4 }}
                    selected={location.pathname === '/configuracion/usuarios'}
                  >
                    <ListItemIcon>
                      <SupervisedUserCircleIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary="Usuarios" />
                  </ListItemButton>
                  <ListItemButton
                    component={RouterLink}
                    to="/configuracion/especialidades"
                    sx={{ pl: 4 }}
                    selected={location.pathname === '/configuracion/especialidades'}
                  >
                    <ListItemIcon>
                      <SchoolIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary="Especialidades" />
                  </ListItemButton>
                  <ListItemButton
                    component={RouterLink}
                    to="/configuracion/programas"
                    sx={{ pl: 4 }}
                    selected={location.pathname === '/configuracion/programas'}
                  >
                    <ListItemIcon>
                      <CategoryIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary="Programas" />
                  </ListItemButton>
                  <ListItemButton
                    component={RouterLink}
                    to="/configuracion/formularios"
                    sx={{ pl: 4 }}
                    selected={location.pathname === '/configuracion/formularios'}
                  >
                    <ListItemIcon>
                      <CategoryIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary="Formularios" />
                  </ListItemButton>
                  <ListItemButton
                    component={RouterLink}
                    to="/configuracion/plantillas"
                    sx={{ pl: 4 }}
                    selected={location.pathname === '/configuracion/plantillas'}
                  >
                    <ListItemIcon>
                      <MessageIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary="Plantillas" />
                  </ListItemButton>
                  <ListItemButton
                    component={RouterLink}
                    to="/configuracion/plantillas-etapas"
                    sx={{ pl: 4 }}
                    selected={location.pathname === '/configuracion/plantillas-etapas'}
                  >
                    <ListItemIcon>
                      <SettingsApplications fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary="Plantillas por Etapa" />
                  </ListItemButton>
                  <ListItemButton
                    component={RouterLink}
                    to="/configuracion/plantillas-formularios"
                    sx={{ pl: 4 }}
                    selected={location.pathname === '/configuracion/plantillas-formularios'}
                  >
                    <ListItemIcon>
                      <Description fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary="Plantillas de Formularios" />
                  </ListItemButton>
                  <ListItemButton
                    component={RouterLink}
                    to="/configuracion/documentos-etapa"
                    sx={{ pl: 4 }}
                    selected={location.pathname === '/configuracion/documentos-etapa'}
                  >
                    <ListItemIcon>
                      <Description fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary="Documentos por Etapa" />
                  </ListItemButton>
                  <ListItemButton
                    component={RouterLink}
                    to="/configuracion/asignacion-internos"
                    sx={{ pl: 4 }}
                    selected={location.pathname === '/configuracion/asignacion-internos'}
                  >
                    <ListItemIcon>
                      <SupervisedUserCircleIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary="Asignación de Internos" />
                  </ListItemButton>
                </List>
              </Collapse>
            </>
          )}
        </List>
      )}
    </>
  );

  return (
    <>
      <Drawer variant="temporary" open={mobileOpen} onClose={handleMobileDrawerToggle} ModalProps={{ keepMounted: true }}
        sx={{ display: { xs: 'block', sm: 'none' }, '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth } }}>
        {drawerContent}
      </Drawer>

      <Drawer variant="permanent" open={open} sx={{
        display: { xs: 'none', sm: 'block' },
        '& .MuiDrawer-paper': {
          boxSizing: 'border-box',
          width: open ? drawerWidth : theme.spacing(7),
          overflowX: 'hidden',
          transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
          borderRight: `1px solid ${theme.palette.divider}`,
        },
      }}>
        {drawerContent}
      </Drawer>
    </>
  );
};

export default Sidebar;